/**
 * useTemplates Hook
 * Manages template CRUD operations with loading and error states.
 * All operations go through the REST API (Lambda functions).
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { templatesApi } from '@/services/templatesApi';
import type {
  TemplateMetadata,
  TemplateResponse,
  SaveTemplateRequest,
} from '@/services/templatesApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('useTemplates');

export interface UseTemplatesReturn {
  templates: TemplateMetadata[];
  isLoading: boolean;
  error: string | null;

  // Operations
  refresh: () => Promise<void>;
  getTemplate: (templateId: string) => Promise<TemplateResponse>;
  createTemplate: (request: SaveTemplateRequest) => Promise<TemplateMetadata>;
  updateTemplate: (templateId: string, request: SaveTemplateRequest) => Promise<TemplateMetadata>;
  deleteTemplate: (templateId: string) => Promise<void>;
  copyDefaultTemplates: (force?: boolean) => Promise<void>;
}

export function useTemplates(): UseTemplatesReturn {
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const [templates, setTemplates] = useState<TemplateMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch templates when authenticated
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setTemplates([]);
      setIsLoading(false);
      return;
    }

    const fetchTemplates = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let data = await templatesApi.listTemplates();
        log.info('Templates loaded', { count: data.length });

        // If no templates, copy default templates for new users
        if (data.length === 0) {
          log.info('No templates found, copying default templates');
          try {
            const copyResult = await templatesApi.copyDefaultTemplates();
            if (copyResult.copied > 0) {
              data = copyResult.templates;
              log.info('Default templates copied', { count: copyResult.copied });
            }
          } catch (copyErr) {
            log.warn('Failed to copy default templates', {
              error: copyErr instanceof Error ? copyErr.message : 'Unknown error'
            });
          }
        }

        setTemplates(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load templates';
        setError(message);
        log.error('Failed to load templates', err instanceof Error ? err : new Error(message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [isAuthenticated, authLoading]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await templatesApi.listTemplates();
      setTemplates(data);
      log.info('Templates refreshed', { count: data.length });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh templates';
      setError(message);
      log.error('Failed to refresh templates', err instanceof Error ? err : new Error(message));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const getTemplate = useCallback(async (templateId: string): Promise<TemplateResponse> => {
    return templatesApi.getTemplate(templateId);
  }, []);

  const createTemplate = useCallback(async (request: SaveTemplateRequest): Promise<TemplateMetadata> => {
    const created = await templatesApi.createTemplate(request);
    setTemplates(prev => [created, ...prev]);
    log.info('Template created', { templateId: created.id });
    return created;
  }, []);

  const updateTemplate = useCallback(async (
    templateId: string,
    request: SaveTemplateRequest
  ): Promise<TemplateMetadata> => {
    const updated = await templatesApi.updateTemplate(templateId, request);
    setTemplates(prev => prev.map(t => t.id === templateId ? updated : t));
    log.info('Template updated', { templateId });
    return updated;
  }, []);

  const deleteTemplate = useCallback(async (templateId: string): Promise<void> => {
    await templatesApi.deleteTemplate(templateId);
    setTemplates(prev => prev.filter(t => t.id !== templateId));
    log.info('Template deleted', { templateId });
  }, []);

  const copyDefaultTemplates = useCallback(async (force: boolean = false): Promise<void> => {
    try {
      const result = await templatesApi.copyDefaultTemplates({ force });
      if (result.copied > 0) {
        await refresh();
        log.info('Default templates copied and list refreshed', { copied: result.copied });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to copy default templates';
      log.error('Failed to copy default templates', err instanceof Error ? err : new Error(message));
      throw err;
    }
  }, [refresh]);

  return {
    templates,
    isLoading,
    error,
    refresh,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyDefaultTemplates,
  };
}
